import Axios from 'axios';
import paths from './endpoints';

import { decodeToken, encodeToken, logout } from '../utilities/auth';
import { Log } from '../utilities/general';

const dispatchUnauthorizedEvent = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('alatpay:unauthorized'));
  }
};

let axiosInterceptorInitialized = false;
let refreshRequestInFlight = null;

const requestTokenRefresh = () => {
  if (refreshRequestInFlight) {
    return refreshRequestInFlight;
  }

  const refreshUrl = `${process.env.REACT_APP_API_BASE_URL}/${paths.refreshToken}`;
  const refreshHeaders = {
    'Ocp-Apim-Subscription-Key': process.env.REACT_APP_APIM_SUB_KEY,
    'Content-Type': 'application/json'
  };

  refreshRequestInFlight = Axios({
    url: refreshUrl,
    method: 'POST',
    headers: refreshHeaders,
    withCredentials: true
  }).finally(() => {
    refreshRequestInFlight = null;
  });

  return refreshRequestInFlight;
};

const setupAxiosInterceptors = () => {
  if (axiosInterceptorInitialized) return;

  Axios.interceptors.response.use(
    (response) => {
      const responseUrl = response?.config?.url || '';
      if (
        responseUrl.includes(paths.login) ||
        responseUrl.includes(paths.refreshToken)
      ) {
        const currentTime = Date.now();
        const expirationTime = currentTime + 60 * 5 * 1000;
        encodeToken('timer', expirationTime);
      }
      return response;
    },
    (err) => Promise.reject(err)
  );

  axiosInterceptorInitialized = true;
};
setupAxiosInterceptors();

const fetchBackend = async (
  endpoint,
  extra,
  method,
  auth,
  body,
  pQuery,
  param,
  multipart,
  responseType,
  nonBaseURL,
  subscriptionKey,
  shouldLogUserOut,
  useDefaultSubKey,
  showUnauthorizedModalOn401,
  signal,
  bearerToken
) => {
  const singleBusiness = decodeToken('sb', '/portal', false);
  const subKey =
    singleBusiness?.subscriptionPrimaryKey ??
    process.env.REACT_APP_APIM_SUB_KEY;
  const environment = decodeToken('env', null, false) || 'live';

  const headers = {
    'Ocp-Apim-Subscription-Key': useDefaultSubKey
      ? process.env.REACT_APP_APIM_SUB_KEY
      : subscriptionKey || subKey,
    'Content-Type': multipart ? 'multipart/form-data' : 'application/json',
    'X-Environment': environment
  };
  const path = paths[endpoint] || endpoint;
  let url = nonBaseURL || `${process.env.REACT_APP_API_BASE_URL}/${path}`;

  if (extra) {
    url += `${extra}`;
  }

  if (param) {
    url += `/${param}`;
  }

  if (pQuery) {
    let paramsArray = Object.keys(pQuery).map(
      (key) =>
        pQuery[key] &&
        `${encodeURIComponent(key)}=${encodeURIComponent(pQuery[key])}`
    );

    paramsArray = paramsArray.filter((item) => item);
    url += `?${paramsArray.join('&')}`;
  }

  // if (auth) {
  //   headers.Authorization = `Bearer ${decodeToken("t")}`;
  // }

  // Explicit override for calls made right after a token-issuing response,
  // before that token has (or should) land in the cookie session.
  if (bearerToken) {
    headers.Authorization = `Bearer ${bearerToken}`;
  }

  const options = {
    url,
    method,
    headers,
    withCredentials: true,
    ...(signal && { signal })
  };

  if (responseType) {
    options.responseType = responseType;
  }

  if (body) {
    options.data = body;
  }

  Log(options);

  // Attempt request, and on 401 try a single refresh + retry
  options.__isRetry = false;
  const attempt = async () => {
    try {
      const res = await Axios(options);
      if (res?.status === 401) {
        throw { response: res };
      }
      return res;
    } catch (err) {
      const status = err?.response?.status;
      if (err?.code === 'ERR_NETWORK') {
        return {
          status: 500,
          code: 'ERR_NETWORK',
          message: 'Kindly check your internet connection and try again.',
          data: {
            status: 500,
            code: 'ERR_NETWORK',
            message: 'Kindly check your internet connection and try again.'
          }
        };
      }
      if (
        status === 401 &&
        !options.__isRetry &&
        !path.includes('refresh-token')
      ) {
        Log('401 encountered — attempting token refresh...');
        try {
          const refreshRes = await requestTokenRefresh();
          if (refreshRes?.status >= 200 && refreshRes?.status < 300) {
            options.__isRetry = true;
            Log('Refresh successful — retrying original request');
            const retriedRes = await Axios(options);
            if (retriedRes?.status === 401) {
              throw { response: retriedRes };
            }
            return retriedRes;
          }
        } catch (refreshErr) {
          Log('Refresh failed', refreshErr?.response || refreshErr);
          if (
            showUnauthorizedModalOn401 &&
            refreshErr?.response?.status === 401
          ) {
            Log('Dispatching unauthorized event due to 401 response');
            dispatchUnauthorizedEvent();
          }
        }
        if (showUnauthorizedModalOn401) {
          Log('Dispatching unauthorized event due to 401 response');
          dispatchUnauthorizedEvent();
        } else if (!path.includes('login') && !path.includes('refresh-token')) {
          auth && shouldLogUserOut && logout('/login');
        }
        return err?.response;
      }
      // Non-401 or already retried
      return (
        err?.response || {
          status: 500,
          data: {
            message: 'An unknown error occurred. Please try again later.'
          }
        }
      );
    }
  };
  return attempt();
};

/**
 *
 * @param {string} endpoint
 * @param {string} extra
 * @param {object} pQuery
 * @param {string} param
 * @param {boolean} auth
 * @param {string} responseType
 * @param {string} nonBaseURL
 * @param {boolean} shouldLogUserOut
 * @param {boolean} useDefaultSubKey
 */
export const get = ({
  endpoint,
  extra = null,
  pQuery,
  param = null,
  auth = true,
  responseType = null,
  nonBaseURL = null,
  subscriptionKey = null,
  shouldLogUserOut = false,
  useDefaultSubKey = false,
  showUnauthorizedModalOn401 = false,
  signal = null
}) =>
  fetchBackend(
    endpoint,
    extra,
    'GET',
    auth,
    null,
    pQuery,
    param,
    null,
    responseType,
    nonBaseURL,
    subscriptionKey,
    shouldLogUserOut,
    useDefaultSubKey,
    showUnauthorizedModalOn401,
    signal
  );

/**
 *
 * @param {string} endpoint
 * @param {string} extra
 * @param {object} body
 * @param {boolean} auth
 * @param {boolean} multipart
 * @param {string} responseType
 * @param {string} nonBaseURL
 * @param {boolean} shouldLogUserOut
 * @param {boolean} useDefaultSubKey
 */
export const post = ({
  // eslint-disable-next-line max-len
  endpoint,
  extra = null,
  body,
  auth = true,
  multipart = false,
  responseType = null,
  nonBaseURL = null,
  subscriptionKey = null,
  pQuery,
  shouldLogUserOut = false,
  useDefaultSubKey = false,
  showUnauthorizedModalOn401 = false,
  bearerToken = null
}) =>
  fetchBackend(
    endpoint,
    extra,
    'POST',
    auth,
    body,
    pQuery,
    null,
    multipart,
    responseType,
    nonBaseURL,
    subscriptionKey,
    shouldLogUserOut,
    useDefaultSubKey,
    showUnauthorizedModalOn401,
    null,
    bearerToken
  );

/**
 *
 * @param {string} endpoint
 * @param {string} extra
 * @param {object} body
 * @param {string} param
 * @param {boolean} auth
 * @param {boolean} multipart
 * @param {string} nonBaseURL
 * @param {string} subscriptionKey
 * @param {boolean} shouldLogUserOut
 * @param {boolean} useDefaultSubKey
 */
export const put = ({
  endpoint,
  extra = null,
  body,
  param,
  auth = true,
  multipart = false,
  nonBaseURL = null,
  subscriptionKey = null,
  shouldLogUserOut = false,
  useDefaultSubKey = false,
  showUnauthorizedModalOn401 = false
}) =>
  fetchBackend(
    endpoint,
    extra,
    'PUT',
    auth,
    body,
    null,
    param,
    multipart,
    null,
    nonBaseURL,
    subscriptionKey,
    shouldLogUserOut,
    useDefaultSubKey,
    showUnauthorizedModalOn401
  );

/**
 *
 * @param {string} endpoint
 * @param {string} extra
 * @param {object} body
 * @param {string} param
 * @param {boolean} auth
 * @param {boolean} multipart
 * @param {string} nonBaseURL
 * @param {string} subscriptionKey
 * @param {boolean} shouldLogUserOut
 * @param {boolean} useDefaultSubKey
 */
export const patch = ({
  endpoint,
  extra = null,
  body,
  param,
  auth = true,
  multipart = false,
  nonBaseURL = null,
  subscriptionKey = null,
  shouldLogUserOut = false,
  useDefaultSubKey = false,
  showUnauthorizedModalOn401 = false
}) =>
  fetchBackend(
    endpoint,
    extra,
    'PATCH',
    auth,
    body,
    null,
    param,
    multipart,
    null,
    nonBaseURL,
    subscriptionKey,
    shouldLogUserOut,
    useDefaultSubKey,
    showUnauthorizedModalOn401
  );

/**
 *
 * @param {string} endpoint
 * @param {string} extra
 * @param {object} body
 * @param {string} param
 * @param {boolean} auth
 * @param {string} nonBaseURL
 * @param {string} subscriptionKey
 * @param {boolean} shouldLogUserOut
 * @param {boolean} useDefaultSubKey
 */
export const del = ({
  endpoint,
  param,
  auth = true,
  extra = null,
  body = null,
  nonBaseURL = null,
  subscriptionKey = null,
  shouldLogUserOut = false,
  useDefaultSubKey = false,
  showUnauthorizedModalOn401 = false
}) =>
  fetchBackend(
    endpoint,
    extra,
    'DELETE',
    auth,
    body,
    null,
    param,
    false,
    null,
    nonBaseURL,
    subscriptionKey,
    shouldLogUserOut,
    useDefaultSubKey,
    showUnauthorizedModalOn401
  );
